import { services } from '@repo/ui/lib/api/services';
import type { SubscriptionConfig } from '@repo/ui/lib/api/services/settings.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Hook pour récupérer la configuration des abonnements
 */
export const useSubscriptionConfig = () => {
  return useQuery<SubscriptionConfig>({
    queryKey: ['subscription-config'],
    queryFn: async () => {
      return await services.settings.getSubscriptionConfig();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook pour mettre à jour la configuration des abonnements
 */
export const useUpdateSubscriptionConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: SubscriptionConfig) => {
      return await services.settings.updateSubscriptionConfig(config);
    },
    onSuccess: () => {
      // Invalider le cache de la configuration
      queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
      // Invalider aussi les caches des abonnements de groupe
      queryClient.invalidateQueries({ queryKey: ['group-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['group-usage-stats'] });
    },
  });
};

/**
 * Hook pour initialiser les paramètres par défaut
 */
export const useInitializeDefaultSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await services.settings.initializeDefaultSettings();
    },
    onSuccess: () => {
      // Invalider le cache de la configuration
      queryClient.invalidateQueries({ queryKey: ['subscription-config'] });
      // Invalider aussi les caches des abonnements de groupe
      queryClient.invalidateQueries({ queryKey: ['group-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['group-usage-stats'] });
    },
  });
};

/**
 * Hook pour obtenir les informations d'un plan spécifique
 */
export const usePlanConfig = (planType: 'BASIC' | 'ADVANCED') => {
  const { data: config } = useSubscriptionConfig();

  return {
    plan: config?.plans[planType],
    isLoading: !config,
    hasConfig: !!config?.plans[planType]
  };
};

/**
 * Hook pour obtenir le prix d'un tier spécifique
 */
export const useTierPrice = (planType: 'BASIC' | 'ADVANCED', maxTrees: number) => {
  const { plan, isLoading } = usePlanConfig(planType);

  const tier = plan?.tiers.find(t => t.maxTrees >= maxTrees);
  const exactTier = plan?.tiers.find(t => t.maxTrees === maxTrees);

  return {
    price: exactTier?.price || tier?.price,
    tier: exactTier || tier,
    isLoading,
    hasExactMatch: !!exactTier
  };
};

/**
 * Hook pour obtenir toutes les options de tiers pour un plan
 */
export const usePlanTiers = (planType: 'BASIC' | 'ADVANCED') => {
  const { plan, isLoading } = usePlanConfig(planType);

  return {
    tiers: plan?.tiers || [],
    isLoading,
    maxAlignments: plan?.maxAlignments ?? 0,
    maxEpaysages: plan?.maxEpaysages ?? 0
  };
};
